import { useCallback, useEffect, useState } from "react";
import { Copy, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";

const ST = { in_attesa: ["In attesa di firma", "amber"], in_firma: ["Firma in corso", "amber"], firmata: ["Firmata", "green"], revocata: ["Revocata", "neutral"] };

const copy = (link) => navigator.clipboard?.writeText(link).then(() => toast.success("Link copiato"));

const RequestDialog = ({ q, open, onClose, onDone }) => {
  const [f, setF] = useState({ email: "", name: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  useEffect(() => {
    if (!open) { return; }
    setLink(null);
    api.get(`/sales/quotes/${q.id}/email-defaults`).then((r) => setF({ email: r.data.to?.[0] || "", name: q.contact_name || "", message: "" })).catch(() => {});
  }, [open, q.id, q.contact_name]);
  const send = async () => {
    setBusy(true);
    try { const r = (await api.post(`/sales/quotes/${q.id}/sign-requests`, { ...f, message: f.message || null })).data; setLink(r.link); toast.success("Richiesta di firma inviata"); onDone(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="sign-request-dialog">
        <DialogHeader><DialogTitle>Richiedi firma da remoto</DialogTitle>
          <DialogDescription>Il cliente riceve un link valido fino alla scadenza dell'offerta. Per firmare inserisce un codice che riceve via email: alla firma l'offerta diventa accettata.</DialogDescription></DialogHeader>
        {link ? <div className="space-y-2" data-testid="sign-request-done"><p className="text-sm text-slate-700">Email inviata a {f.email}. Puoi anche copiare il link:</p>
          <div className="flex gap-2"><Input readOnly value={link} data-testid="sign-request-link" /><Button variant="outline" onClick={() => copy(link)} data-testid="sign-request-copy"><Copy /></Button></div></div>
          : <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Nome del firmatario</Label><Input maxLength={120} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sign-request-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" maxLength={200} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="sign-request-email" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Messaggio (facoltativo)</Label><Textarea rows={3} maxLength={2000} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="sign-request-message" /></div>
          </div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>{link ? "Chiudi" : "Annulla"}</Button>
          {!link && <Button disabled={busy || f.name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(f.email)} onClick={send} data-testid="sign-request-send">{busy ? "Invio…" : "Invia richiesta"}</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SignRequestButton = ({ q, canUpdate, onDone }) => {
  const [open, setOpen] = useState(false);
  if (!canUpdate || !["bozza", "inviata"].includes(q.status) || q.expired) { return null; }
  return (<>
    <Button variant="outline" className="gap-1" onClick={() => setOpen(true)} data-testid="quote-sign-request"><PenLine />Richiedi firma</Button>
    <RequestDialog q={q} open={open} onClose={() => setOpen(false)} onDone={onDone} />
  </>);
};

export const SignRequestsPanel = ({ q, canUpdate, reloadKey, onDone }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/sales/quotes/${q.id}/sign-requests`).then((r) => setItems(r.data.items)).catch(() => {}), [q.id]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!items.length) { return null; }
  const revoke = async (id) => {
    try { await api.post(`/sales/quotes/${q.id}/sign-requests/${id}/revoke`); toast.success("Link revocato"); load(); onDone?.(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Panel className="mt-4 p-4" data-testid="sign-requests-panel"><SectionLabel>Firma da remoto</SectionLabel>
      <ul className="mt-2 divide-y divide-slate-100">{items.map((r) => <li key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`sign-request-${r.id}`}>
        <StatusBadge tone={ST[r.status]?.[1] || "neutral"}>{ST[r.status]?.[0] || r.status}</StatusBadge>
        <span className="flex-1 text-slate-700">{r.name} · {r.email}<span className="block text-xs text-slate-500">
          {r.status === "firmata" ? `Firmata da ${r.signer_name} il ${fmtDateTime(r.signed_at)}` : `Inviata il ${fmtDateTime(r.created_at)} da ${r.requested_by_name || "—"}`}
          {r.reminded_at && r.status !== "firmata" ? ` · promemoria inviato il ${fmtDateTime(r.reminded_at)}` : ""}</span></span>
        {r.status === "in_attesa" && canUpdate && <Button size="sm" variant="ghost" onClick={() => window.confirm("Revocare il link di firma?") && revoke(r.id)} data-testid={`sign-request-revoke-${r.id}`}>Revoca</Button>}
      </li>)}</ul>
    </Panel>
  );
};
